import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  CircularProgress,
  Tooltip,
  FormControlLabel,
  Checkbox,
  Divider,
  Switch,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Visibility as ViewIcon,
  RemoveCircleOutline as RemoveIcon,
} from '@mui/icons-material';
import { TimePicker } from '@mui/x-date-pickers/TimePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
// [Web UX 9 / T2, 2026-10-13] a dátumválasztók magyar feliratai (Cancel/OK/Select date helyett) — lásd utils/dateLocale.ts
import { HU_PICKER_LOCALE_TEXT } from '../utils/dateLocale';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { hu } from 'date-fns/locale';
import { useAuth } from '../contexts/AuthContext';
import { useConfirm } from '../contexts/ConfirmContext';
import { useFeedback } from '../contexts/FeedbackContext';
import { venueService } from '../services/venueService';
// [3. javító kör, F-1, 2026-10-03] lejárat-tudatos burkolók (`utils/saveErrors.ts`)
import { requestErrorMessage, saveErrorMessage } from '../utils/saveErrors';
import { useUnsavedChanges } from '../contexts/UnsavedChangesContext';
import { isFormDirty, discardChangesConfirm } from '../utils/formDirty';
import { isForintInputAccepted, parseForintInput, forintFieldValue } from '../utils/money';
import { Venue, TimeSlot } from '../types/venue';

const VenueManagement: React.FC = () => {
  const { user: currentUser } = useAuth();
  const confirm = useConfirm();
  const { showSuccess, showError } = useFeedback();
  const [venues, setVenues] = useState<Venue[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string>('');
  const [selectedVenue, setSelectedVenue] = useState<Venue | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogType, setDialogType] = useState<'create' | 'edit' | 'view'>('view');

  useEffect(() => {
    loadVenues();
  }, []);

  const loadVenues = async () => {
    try {
      setLoading(true);
      setError('');
      const venueData = await venueService.getAllVenues();
      setVenues(venueData);
    } catch (err: any) {
      setError(requestErrorMessage(err, 'Hiba történt a helyszínek betöltésekor'));
    } finally {
      setLoading(false);
    }
  };

  const openDialog = (type: typeof dialogType, venue?: Venue) => {
    setDialogType(type);
    setSelectedVenue(venue || null);
    setDialogOpen(true);
  };

  const closeDialog = () => {
    setDialogOpen(false);
    setSelectedVenue(null);
  };

  const handleVenueAction = async (action: 'create' | 'edit', data?: any) => {
    try {
      setError('');

      switch (action) {
        case 'create':
          await venueService.createVenue(data);
          showSuccess('A helyszín létrehozva.');
          break;
        case 'edit':
          if (selectedVenue) {
            await venueService.updateVenue(selectedVenue.venueId, data);
            showSuccess('A helyszín módosítva.');
          }
          break;
      }

      closeDialog();
      await loadVenues();
    } catch (err: any) {
      // Egységes hibaszöveg (szerver-üzenet / hálózati / szerverhiba), mint a többi képernyőn.
      // [validátor M-1, 2026-10-03] lejárt munkamenet → az újra-belépő ablakkal egyező mondat
      const message = saveErrorMessage(err, 'Hiba történt a művelet során');
      setError(message);
      showError(message);
    }
  };

  const handleDeleteVenue = async (venue: Venue) => {
    const confirmed = await confirm({
      title: 'Helyszín törlése',
      message: (
        <>
          Biztosan törölni szeretnéd a <strong>{venue.name}</strong> helyszínt?
          Ez deaktiválja a helyszínt a rendszerben.
        </>
      ),
      confirmLabel: 'Törlés',
      destructive: true,
    });
    if (!confirmed) return;

    try {
      setError('');
      await venueService.deleteVenue(venue.venueId);
      showSuccess('A helyszín törölve.');
      await loadVenues();
    } catch (err: any) {
      const message = requestErrorMessage(err, 'Hiba történt a művelet során');
      setError(message);
      showError(message);
    }
  };

  const isAdmin = currentUser?.userRole === 'Administrator';

  if (!isAdmin) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">
          Nincs jogosultságod a helyszínek kezeléséhez.
        </Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom>
        Helyszín kezelés
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      <Paper sx={{ p: 2, mb: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6">
            Helyszínek ({venues.length})
          </Typography>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => openDialog('create')}
          >
            Új helyszín
          </Button>
        </Box>
      </Paper>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Név</TableCell>
              <TableCell>Cím</TableCell>
              <TableCell>Elérhetőség</TableCell>
              <TableCell>Asztalok száma</TableCell>
              <TableCell>Asztalfoglalás</TableCell>
              <TableCell>Kapcsolattartó</TableCell>
              <TableCell>Állapot</TableCell>
              <TableCell align="right">Műveletek</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={8} align="center">
                  <CircularProgress />
                </TableCell>
              </TableRow>
            ) : venues.length === 0 ? (
              <TableRow>
                <TableCell colSpan={8} align="center">
                  Nincs találat
                </TableCell>
              </TableRow>
            ) : (
              venues.map((venue) => (
                <TableRow key={venue.venueId}>
                  <TableCell>{venue.name}</TableCell>
                  <TableCell>{venue.address || '-'}</TableCell>
                  <TableCell>{venue.availability || '-'}</TableCell>
                  <TableCell>{venue.tableCount} asztal</TableCell>
                  <TableCell>
                    <Tooltip title={
                      venue.tableReservationEnabled
                        ? (venue.tableReservationTimeSlots && venue.tableReservationTimeSlots.length > 0
                          ? (() => {
                              const dayNames = ['Vas', 'Hét', 'Kedd', 'Szer', 'Csüt', 'Pén', 'Szom'];
                              return venue.tableReservationTimeSlots
                                .map(s => `${s.dayOfWeek !== undefined ? dayNames[s.dayOfWeek] + ' ' : ''}${s.startTime}-${s.endTime}`)
                                .join(', ');
                            })()
                          : 'Engedélyezett (bármikor)')
                        : 'Letiltva'
                    }>
                      <Chip
                        label={venue.tableReservationEnabled ? 'Igen' : 'Nem'}
                        color={venue.tableReservationEnabled ? 'success' : 'default'}
                        size="small"
                      />
                    </Tooltip>
                  </TableCell>
                  <TableCell>
                    {venue.contactName ? (
                      <Box>
                        <Typography variant="body2">{venue.contactName}</Typography>
                        {venue.contactPhone && (
                          <Typography variant="caption" color="text.secondary">
                            {venue.contactPhone}
                          </Typography>
                        )}
                      </Box>
                    ) : '-'}
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={venue.isActive ? 'Aktív' : 'Inaktív'}
                      color={venue.isActive ? 'success' : 'default'}
                      size="small"
                    />
                  </TableCell>
                  <TableCell align="right">
                    <Tooltip title="Megtekintés">
                      <IconButton
                        size="small"
                        onClick={() => openDialog('view', venue)}
                      >
                        <ViewIcon />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Szerkesztés">
                      <IconButton
                        size="small"
                        onClick={() => openDialog('edit', venue)}
                      >
                        <EditIcon />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Törlés">
                      <IconButton
                        size="small"
                        onClick={() => handleDeleteVenue(venue)}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Venue Dialog */}
      <VenueDialog
        open={dialogOpen}
        onClose={closeDialog}
        type={dialogType}
        venue={selectedVenue}
        onAction={handleVenueAction}
      />
    </Box>
  );
};

interface VenueDialogProps {
  open: boolean;
  onClose: () => void;
  type: 'create' | 'edit' | 'view';
  venue: Venue | null;
  onAction: (action: 'create' | 'edit', data?: any) => void;
}

const VenueDialog: React.FC<VenueDialogProps> = ({
  open,
  onClose,
  type,
  venue,
  onAction,
}) => {
  const [formData, setFormData] = useState<any>({
    name: '',
    address: '',
    availability: '',
    monthlyCost: '',
    contactName: '',
    contactPhone: '',
    contactEmail: '',
    tableCount: '',
    notes: '',
    isActive: true,
    tableReservationEnabled: false,
    tableReservationTimeSlots: [] as TimeSlot[],
    allowTableReservationDuringGroupTraining: false,
  });
  const [formError, setFormError] = useState('');
  /**
   * A megnyitáskori (betöltött vagy üres) helyszín-adatok, mint alapvonal a mentetlen-változás
   * védelemhez. `null` = még nem állt össze az űrlap → soha nem piszkos.
   */
  const [baseline, setBaseline] = useState<any>(null);
  const confirm = useConfirm();

  useEffect(() => {
    setFormError('');
  }, [open, type, venue]);

  /**
   * Az űrlap NULLÁZÁSA + az alapvonal felvétele a dialógus MEGNYITÁSAKOR.
   *
   * ⚠️ Az `open` a függőségek között NEM elhagyható (ugyanaz a hiba, mint a `UserDialog`-ban volt):
   * az „Uj helyszín” gomb csak az `open`-t állítja át (a `type` már `'create'`, a `venue` már `null`
   * egy előző create után), tehát `open` nélkül a hatás NEM futna újra — a félbehagyott, majd
   * ELVETETT űrlap értékei visszajönnének a következő megnyitáskor (az admin egy eldobott
   * helyszínt menthetne el), és az üres alapvonalhoz képest AZONNAL „mentetlennek” látszódnának,
   * tehát az ÉRINTETLEN űrlap bezárása is rákérdezne (hamis riasztás → a védelem devalválódik).
   * Az alapvonal SZÁNDÉKOSAN ugyanebben a hatásban áll fel, mint a `formData` — külön hatásban
   * a kettő elcsúszhatna egymástól.
   */
  useEffect(() => {
    if (!open) return;
    if (venue && (type === 'edit' || type === 'view')) {
      const loaded = {
        name: venue.name,
        address: venue.address || '',
        availability: venue.availability || '',
        // ⚠️ SZÖVEGKÉNT [BL-1, 2026-09-13]: a két szám-mező `onChange`-e NYERS szöveget ír vissza
        // (`e.target.value`), tehát ha az alapvonalba a szerver SZÁMA kerül, a `13` és a `"13"`
        // eltérése miatt az „átírtam, majd visszaírtam az eredetit" állapot sosem tér tisztára
        // (mérve: `/api/venues` → `tableCount: 13`, 13→14→13 után is piszkos maradt). A `formDirty`
        // szám-egyenértékűsége ezt önmagában is elfedi — ez a KETTŐS védelem másik fele: az
        // alapvonal ugyanabban az alakban áll, amit a mező ír. A mentés `parseForintInput`-tal
        // küldi tovább (lásd `handleSubmit`).
        //
        // ⚠️ A „Havi költség" VÁZLAT-tartó pénz-mező [BL-A, 2026-09-14]: a mező NYERS SZÖVEGET tart
        // (nem számot), tehát az alapvonalnak is szövegnek KELL lennie — különben visszatér a BL-1.
        // `forintFieldValue`, NEM `venue.monthlyCost ? String(...) : ''` [N-12]: a `0` FALSY, tehát
        // a korábbi alak a nulla forintos havi költséget ÜRES mezőnek mutatta, és a mentés némán
        // `null`-ra írta át — a nulla viszont érdemi érték („ingyenes terem"), lásd `money.ts`.
        monthlyCost: forintFieldValue(venue.monthlyCost),
        contactName: venue.contactName || '',
        contactPhone: venue.contactPhone || '',
        contactEmail: venue.contactEmail || '',
        tableCount: venue.tableCount ? String(venue.tableCount) : '',
        notes: venue.notes || '',
        isActive: venue.isActive,
        tableReservationEnabled: venue.tableReservationEnabled || false,
        tableReservationTimeSlots: venue.tableReservationTimeSlots || [],
        allowTableReservationDuringGroupTraining: venue.allowTableReservationDuringGroupTraining || false,
      };
      setFormData(loaded);
      setBaseline(loaded);
    } else if (type === 'create') {
      const empty = {
        name: '',
        address: '',
        availability: '',
        monthlyCost: '',
        contactName: '',
        contactPhone: '',
        contactEmail: '',
        // Szövegként, ahogy a mező írja (lásd az edit-ág BL-1 magyarázatát).
        tableCount: '10',
        notes: '',
        isActive: true,
        tableReservationEnabled: false,
        tableReservationTimeSlots: [],
        allowTableReservationDuringGroupTraining: false,
      };
      setFormData(empty);
      setBaseline(empty);
    }
  }, [open, venue, type]);

  const handleInputChange = (field: string, value: any) => {
    setFormData((prev: any) => ({ ...prev, [field]: value }));
  };

  // Helper function to parse time string (HH:mm) to Date object
  const parseTimeString = (timeStr: string): Date => {
    const [hours, minutes] = timeStr.split(':').map(Number);
    const date = new Date();
    date.setHours(hours, minutes, 0, 0);
    return date;
  };

  const DAY_NAMES = ['Vasárnap', 'Hétfő', 'Kedd', 'Szerda', 'Csütörtök', 'Péntek', 'Szombat'];
  const DAY_ORDER = [1, 2, 3, 4, 5, 6, 0]; // Mon–Sun display order

  const addTimeSlotForDay = (dayOfWeek: number) => {
    setFormData((prev: any) => ({
      ...prev,
      tableReservationTimeSlots: [...prev.tableReservationTimeSlots, { dayOfWeek, startTime: '', endTime: '' }],
    }));
  };

  const removeTimeSlot = (index: number) => {
    setFormData((prev: any) => ({
      ...prev,
      tableReservationTimeSlots: prev.tableReservationTimeSlots.filter((_: TimeSlot, i: number) => i !== index),
    }));
  };

  const updateTimeSlot = (index: number, field: 'startTime' | 'endTime', value: string) => {
    setFormData((prev: any) => ({
      ...prev,
      tableReservationTimeSlots: prev.tableReservationTimeSlots.map((slot: TimeSlot, i: number) =>
        i === index ? { ...slot, [field]: value } : slot
      ),
    }));
  };

  const handleSubmit = () => {
    setFormError('');
    const tableCount = Number(formData.tableCount);
    if (!tableCount || tableCount < 1 || tableCount > 100) {
      setFormError('Az asztalok száma kötelező, és 1 és 100 között kell lennie.');
      return;
    }

    // Validate time slots if enabled
    if (formData.tableReservationEnabled && formData.tableReservationTimeSlots.length > 0) {
      const invalidSlots = formData.tableReservationTimeSlots.some(
        (slot: TimeSlot) => !slot.startTime || !slot.endTime || slot.startTime >= slot.endTime
      );
      if (invalidSlots) {
        setFormError('Minden időintervallumnak érvényesnek kell lennie (kezdés < befejezés).');
        return;
      }
    }

    /**
     * A HAVI KÖLTSÉG a közös forint-értelmezőn megy ki [BL-A, 2026-09-14]: a `Number()` a magyarul
     * gépelt „120.000"-ből 120-at csinált, NÉMÁN (mérve, lásd a mező kommentjét).
     *
     * A `Number()` TARTALÉK-ág szándékos, és CSAK a mező által elő sem állítható tartalomra fut:
     * a bevitel-szűrő óta a mezőbe csak számjegy kerülhet be, tehát nem-számjegy tartalom KIZÁRÓLAG
     * a szerverről betöltött, RÉGI tizedes értékből származhat (`String(22000.5)` → `"22000.5"`).
     * Ha ilyenkor `null`-t küldenénk, egy ÉRINTETLEN mező mellett menne veszendőbe a klub adata —
     * ezért a régi értéket a korábbi (`Number`) úton visszük tovább. Nem lesz `NaN`: csak akkor
     * megy ki szám, ha véges.
     */
    const rawMonthlyCost = String(formData.monthlyCost ?? '');
    const parsedMonthlyCost = parseForintInput(rawMonthlyCost);
    const legacyMonthlyCost = Number(rawMonthlyCost);
    const monthlyCost =
      rawMonthlyCost === ''
        ? null
        : parsedMonthlyCost ?? (Number.isFinite(legacyMonthlyCost) ? legacyMonthlyCost : null);

    const submitData = {
      ...formData,
      monthlyCost,
      tableCount: tableCount,
      address: formData.address || null,
      availability: formData.availability || null,
      contactName: formData.contactName || null,
      contactPhone: formData.contactPhone || null,
      contactEmail: formData.contactEmail || null,
      notes: formData.notes || null,
      tableReservationTimeSlots: formData.tableReservationTimeSlots.length > 0
        ? formData.tableReservationTimeSlots.filter((slot: TimeSlot) => slot.startTime && slot.endTime)
        : null,
    };
    onAction(type as 'create' | 'edit', submitData);
  };

  const getTitle = () => {
    switch (type) {
      case 'create': return 'Új helyszín létrehozása';
      case 'edit': return 'Helyszín szerkesztése';
      case 'view': return 'Helyszín adatai';
      default: return '';
    }
  };

  const isViewMode = type === 'view';

  /**
   * Mentetlen-változás védelem. Megtekintő módban minden mező tiltva, ott nem lehet piszkos.
   * `open &&`: a dialógus zárva is mountolva marad, az utolsó tartalommal.
   */
  const isDirty = open && !isViewMode && isFormDirty(baseline, formData);
  useUnsavedChanges(isDirty, 'Helyszín adatai');

  /** A Dialog `onClose`-a (backdrop + ESC) és a „Bezárás" gomb közös, védett záró útja. */
  const handleClose = async () => {
    if (isDirty && !(await confirm(discardChangesConfirm('helyszín űrlapja')))) {
      return;
    }
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>{getTitle()}</DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            {formError && (
              <Alert severity="error" onClose={() => setFormError('')}>
                {formError}
              </Alert>
            )}
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
              <TextField
                sx={{ flex: 1, minWidth: 200 }}
                label="Helyszín neve *"
                value={formData.name}
                onChange={(e) => handleInputChange('name', e.target.value)}
                disabled={isViewMode}
                required
              />
              <TextField
                sx={{ flex: 1, minWidth: 200 }}
                label="Cím"
                value={formData.address}
                onChange={(e) => handleInputChange('address', e.target.value)}
                disabled={isViewMode}
                placeholder="pl. 1234 Budapest, Példa utca 1."
              />
            </Box>
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
              <TextField
                sx={{ flex: 1, minWidth: 200 }}
                label="Elérhetőség (tól-ig)"
                value={formData.availability}
                onChange={(e) => handleInputChange('availability', e.target.value)}
                disabled={isViewMode}
                placeholder="pl. 8:00-22:00"
              />
              <TextField
                sx={{ flex: 1, minWidth: 200 }}
                label="Havi költség (Ft)"
                /*
                 * `type="text"` + `inputMode="numeric"`, NEM `type="number"` [BL-A, 2026-09-14]:
                 * ez volt a NYOLCADIK pénz-mező, ami kimaradt a közös forint-szabályból
                 * (`utils/money.ts`), és karakterre ugyanabba a néma, EZERSZERES hibába futott.
                 * MÉRVE a valódi felületen (Chromium hu-HU → :3000 → :5229 → DB, valódi
                 * leütésekkel, MENTÉSSEL, majd `GET /api/venues/{id}`-ből visszaolvasva):
                 *   gépelt "120.000" -> a MEZŐBEN "120.000" -> PUT {"monthlyCost":120} -> 120
                 *   gépelt "120,000" -> a MEZŐBEN "120.000" -> PUT {"monthlyCost":120} -> 120
                 * (a magyar vesszőt maga a böngésző írta át pontra a szám-inputban).
                 * A mező NYERS SZÖVEGET tart (vázlat), a szűrő a nem-számjegy leütést EL SEM
                 * tárolja; a MOSTANI érték is megy [J-3], hogy egy régi, tizedes havi költség
                 * takarítható legyen. Az őr-teszt: `tests/e2e/flow-venue-money.spec.ts`.
                 */
                type="text"
                value={formData.monthlyCost}
                onChange={(e) => {
                  const value = e.target.value;
                  if (!isForintInputAccepted(value, String(formData.monthlyCost ?? ''))) return;
                  handleInputChange('monthlyCost', value);
                }}
                disabled={isViewMode}
                inputProps={{ inputMode: 'numeric', 'data-testid': 'venue-monthly-cost-input' }}
              />
            </Box>
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
              <TextField
                sx={{ flex: 1, minWidth: 200 }}
                label="Asztalok száma *"
                type="number"
                value={formData.tableCount}
                onChange={(e) => handleInputChange('tableCount', e.target.value)}
                disabled={isViewMode}
                required
                inputProps={{ min: 1, max: 100 }}
                helperText="1-100 között (kötelező)"
              />
            </Box>
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
              <TextField
                sx={{ flex: 1, minWidth: 200 }}
                label="Kapcsolattartó neve"
                value={formData.contactName}
                onChange={(e) => handleInputChange('contactName', e.target.value)}
                disabled={isViewMode}
              />
              <TextField
                sx={{ flex: 1, minWidth: 200 }}
                label="Kapcsolattartó telefonja"
                value={formData.contactPhone}
                onChange={(e) => handleInputChange('contactPhone', e.target.value)}
                disabled={isViewMode}
              />
            </Box>
            <TextField
              fullWidth
              label="Kapcsolattartó e-mail"
              type="email"
              value={formData.contactEmail}
              onChange={(e) => handleInputChange('contactEmail', e.target.value)}
              disabled={isViewMode}
            />
            <TextField
              fullWidth
              label="Megjegyzések"
              multiline
              rows={3}
              value={formData.notes}
              onChange={(e) => handleInputChange('notes', e.target.value)}
              disabled={isViewMode}
            />

            {/* Active Status */}
            {type === 'edit' && (
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.isActive}
                    onChange={(e) => handleInputChange('isActive', e.target.checked)}
                    disabled={isViewMode}
                    color="success"
                  />
                }
                label="Helyszín aktív"
              />
            )}

            {/* Table Reservation Settings */}
            <Divider sx={{ my: 2 }} />
            <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 1 }}>
              Asztalfoglalás beállítások
            </Typography>

            <FormControlLabel
              control={
                <Checkbox
                  checked={formData.tableReservationEnabled}
                  onChange={(e) => handleInputChange('tableReservationEnabled', e.target.checked)}
                  disabled={isViewMode}
                />
              }
              label="Asztalfoglalás engedélyezése ezen a helyszínen"
            />

            {formData.tableReservationEnabled && (
              <>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={formData.allowTableReservationDuringGroupTraining}
                      onChange={(e) => handleInputChange('allowTableReservationDuringGroupTraining', e.target.checked)}
                      disabled={isViewMode}
                    />
                  }
                  label="Asztalfoglalás engedélyezése csoportos edzés közben"
                />

                <Box sx={{ mt: 2 }}>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                    Foglalható időintervallumok naponként (ha egy naphoz nincs intervallum megadva, azon a napon nem lehet foglalni):
                  </Typography>

                  <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={hu} localeText={HU_PICKER_LOCALE_TEXT}>
                    {DAY_ORDER.map((dayOfWeek) => {
                      const slotsForDay = formData.tableReservationTimeSlots
                        .map((slot: TimeSlot, idx: number) => ({ slot, idx }))
                        .filter(({ slot }: { slot: TimeSlot }) => slot.dayOfWeek === dayOfWeek);
                      return (
                        <Box key={dayOfWeek} sx={{ mb: 1.5 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                            <Typography variant="body2" sx={{ minWidth: 90, fontWeight: 500 }}>
                              {DAY_NAMES[dayOfWeek]}
                            </Typography>
                            {!isViewMode && (
                              <Button
                                size="small"
                                startIcon={<AddIcon />}
                                onClick={() => addTimeSlotForDay(dayOfWeek)}
                                sx={{ py: 0 }}
                              >
                                Hozzáad
                              </Button>
                            )}
                            {slotsForDay.length === 0 && (
                              <Typography variant="caption" color="text.disabled">
                                – zárva
                              </Typography>
                            )}
                          </Box>
                          {slotsForDay.map(({ slot, idx }: { slot: TimeSlot; idx: number }) => (
                            <Box key={idx} sx={{ display: 'flex', gap: 1, mb: 0.5, ml: 12, alignItems: 'center' }}>
                              <TimePicker
                                label="Kezdés"
                                value={slot.startTime ? parseTimeString(slot.startTime) : null}
                                onChange={(time) => {
                                  if (time) {
                                    const dt = time as Date;
                                    updateTimeSlot(idx, 'startTime', `${dt.getHours().toString().padStart(2, '0')}:${dt.getMinutes().toString().padStart(2, '0')}`);
                                  }
                                }}
                                disabled={isViewMode}
                                ampm={false}
                                slotProps={{ textField: { size: 'small', sx: { width: 130 } } }}
                              />
                              <Typography>–</Typography>
                              <TimePicker
                                label="Befejezés"
                                value={slot.endTime ? parseTimeString(slot.endTime) : null}
                                onChange={(time) => {
                                  if (time) {
                                    const dt = time as Date;
                                    updateTimeSlot(idx, 'endTime', `${dt.getHours().toString().padStart(2, '0')}:${dt.getMinutes().toString().padStart(2, '0')}`);
                                  }
                                }}
                                disabled={isViewMode}
                                ampm={false}
                                slotProps={{ textField: { size: 'small', sx: { width: 130 } } }}
                              />
                              {!isViewMode && (
                                <IconButton size="small" color="error" onClick={() => removeTimeSlot(idx)}>
                                  <RemoveIcon />
                                </IconButton>
                              )}
                            </Box>
                          ))}
                        </Box>
                      );
                    })}
                  </LocalizationProvider>
                </Box>
              </>
            )}
          </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>
          Bezárás
        </Button>
        {!isViewMode && (
          <Button
            onClick={handleSubmit}
            variant="contained"
            color="primary"
          >
            {type === 'create' ? 'Létrehozás' : 'Mentés'}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default VenueManagement;